// Package-level roll-up of the LIVE interest-modeling preview into the Executive
// Summary's Target / Max contribution sentence. Pure + unit-tested: it bridges the
// two per-kind preview surfaces — the carried-interest portfolio (net-to-us) and the
// per-tract UMI elections (the elected outcome's NPV / undiscounted net) — into one
// `{ targetDelta, maxDelta }` the Executive Summary states in prose.
//
// The rule: a carry position contributes its portfolio net-to-us to BOTH Target and
// Max; each ELECTED election contributes its NPV to Target and its undiscounted net
// to Max; an un-elected election contributes nothing. This mirrors the working-config
// surface the analyst sees, so the summary tracks the live edits (not the last
// committed run).
import {
  portfolioKpis,
  type CarrySeriesPoint,
} from './interest-modeling-preview';
import {
  electionGroupsOf,
  electionResultKey,
  type ElectionResult,
} from './interest-modeling-election';
import type { InterestItem } from './types';

/** The interest-modeling contribution to the package Target / Max value ($). */
export interface InterestModelDelta {
  /** Carry net-to-us + Σ each elected election's NPV. */
  targetDelta: number;
  /** Carry net-to-us + Σ each elected election's undiscounted net (net-to-owner). */
  maxDelta: number;
}

/** The zero contribution — the default before any position is modeled. */
export const EMPTY_INTEREST_MODEL_DELTA: InterestModelDelta = {
  targetDelta: 0,
  maxDelta: 0,
};

/**
 * Rolls the live carry portfolio + the elected UMI outcomes up into the package
 * Target / Max contribution. Carry net-to-us seeds both deltas; each ELECTED election
 * whose outcome is cached adds its NPV to Target and its undiscounted net to Max.
 * Un-elected elections (and elected ones with no cached result yet) contribute
 * nothing.
 *
 * @param {CarrySeriesPoint[]} portfolio - The recombined carry portfolio series (its net-to-us is the carry contribution).
 * @param {InterestItem[]} interestItems - The working-config positions (drives which elections are elected).
 * @param {ReadonlyMap<string, ElectionResult[]>} electionResults - Live per-tract election outcomes, keyed by tract id.
 * @return {InterestModelDelta} The Target / Max contribution.
 */
export function computeInterestModelDelta(
  portfolio: CarrySeriesPoint[],
  interestItems: InterestItem[],
  electionResults: ReadonlyMap<string, ElectionResult[]>
): InterestModelDelta {
  const carryNetToUs = portfolioKpis(portfolio).netToUs;
  let targetDelta = carryNetToUs;
  let maxDelta = carryNetToUs;
  for (const item of interestItems) {
    if (item.kind !== 'election') {
      continue;
    }
    // Each property group elects its own outcome and contributes its own figures, so
    // the tract's contribution is the SUM over its elected groups (INS-10673) — not
    // one outcome for the whole tract.
    for (const group of electionGroupsOf(item)) {
      if (!group.electedElection) {
        continue;
      }
      const result = electionResults
        .get(electionResultKey(item.tractId, group.id))
        ?.find((entry) => entry.election === group.electedElection);
      if (result) {
        targetDelta += result.npv;
        maxDelta += result.netToOwner;
      }
    }
  }
  return { targetDelta, maxDelta };
}

/**
 * Whether the roll-up is worth stating — a non-zero contribution to either Target or
 * Max. Mirrors the mock's `hasModelDelta` guard: no contributing position ⇒ the
 * Executive Summary omits the sentence entirely (no "$0" line).
 *
 * @param {InterestModelDelta} delta - The rolled-up contribution.
 * @return {boolean} True when either delta is non-zero.
 */
export function hasInterestModelDelta(delta: InterestModelDelta): boolean {
  return delta.targetDelta !== 0 || delta.maxDelta !== 0;
}
